import { AssignedConversation, Conversation } from "@doktor-se/bones-ui/dist/web-shared/types"

import { Booking } from "reducers/booking"
import { BookedAppointment, ReassignRequestConversation } from "reducers/conversations/conversations.reducer"
import { QueueConversation, SearchPageConversation } from "types"

// Union type that covers all conversation types used in the application
type AnyConversation =
  | SearchPageConversation
  | Conversation
  | AssignedConversation
  | QueueConversation
  | BookedAppointment
  | ReassignRequestConversation

// Category and booking type interfaces for type safety
interface Category {
  name: string
}

interface BookingType {
  id: string
  localisedNames: Record<string, string>
}

interface SortUtilsContext {
  categories?: Record<string, Category>
  bookingTypes?: BookingType[]
  appLanguage?: string
}

// Enhanced interfaces for better type safety
interface ConversationWithProfile {
  profile?: {
    type: "child"
    name?: string
    data?: {
      securityNumber: string
    }
  }
}

interface ConversationWithAccount {
  account?: {
    displayName?: string
    pnr?: string
  }
}

interface ConversationWithMetadata {
  customerMetadata?: {
    listedAtClinicName?: any
    otherJournal?: any
    medicationList?: any
  }
}

interface ConversationWithAssignment {
  assignedStaffId?: string | null
}

interface ConversationWithQueue {
  queuePriority?: string
}

interface ConversationWithCategory {
  categoryId?: number
}

interface ConversationWithTiming {
  created?: string
  snoozedUntil?: string | null
}

interface ConversationWithBooking {
  bookingStart?: string
  bookingFilter?: string
  metadata?: {
    bookingId?: string
  }
}

// Type guards for better type safety
const hasProfile = (conv: AnyConversation): conv is AnyConversation & ConversationWithProfile => {
  return "profile" in conv && conv.profile !== undefined
}

const hasAccount = (conv: AnyConversation): conv is AnyConversation & ConversationWithAccount => {
  return "account" in conv && conv.account !== undefined
}

const hasMetadata = (conv: AnyConversation): conv is AnyConversation & ConversationWithMetadata => {
  return "customerMetadata" in conv && conv.customerMetadata !== undefined
}

const hasAssignment = (conv: AnyConversation): conv is AnyConversation & ConversationWithAssignment => {
  return "assignedStaffId" in conv
}

const hasQueue = (conv: AnyConversation): conv is AnyConversation & ConversationWithQueue => {
  return "queuePriority" in conv
}

const hasCategory = (conv: AnyConversation): conv is AnyConversation & ConversationWithCategory => {
  return "categoryId" in conv
}

const hasTiming = (conv: AnyConversation): conv is AnyConversation & ConversationWithTiming => {
  return "created" in conv || "snoozedUntil" in conv
}

const hasBooking = (conv: AnyConversation): conv is AnyConversation & ConversationWithBooking => {
  return "bookingStart" in conv || "bookingFilter" in conv
}

/**
 * Common sort value functions that can be reused across conversation list components
 */
export const sortValueFunctions = {
  /**
   * Extract PNR/security number for sorting
   */
  pnr: (conv: AnyConversation): string => {
    if (hasProfile(conv) && conv.profile?.type === "child") {
      return conv.profile?.data?.securityNumber || ""
    }
    if (hasAccount(conv)) {
      return conv.account?.pnr || ""
    }
    return ""
  },

  /**
   * Extract display name for sorting
   */
  name: (conv: AnyConversation): string => {
    if (hasProfile(conv) && conv.profile?.type === "child") {
      return conv.profile?.name?.toLowerCase?.() || ""
    }
    if (hasAccount(conv)) {
      return conv.account?.displayName?.toLowerCase?.() || ""
    }
    return ""
  },

  /**
   * Extract listed status for sorting
   */
  listed: (conv: AnyConversation): number => {
    if (hasMetadata(conv)) {
      return triStateSortValue(conv.customerMetadata?.listedAtClinicName)
    }
    return -1
  },

  /**
   * Extract record consent for sorting
   */
  recordConsent: (conv: AnyConversation): number => {
    if (hasMetadata(conv)) {
      return triStateSortValue(conv.customerMetadata?.otherJournal)
    }
    return -1
  },

  /**
   * Extract medication consent for sorting
   */
  medicationConsent: (conv: AnyConversation): number => {
    if (hasMetadata(conv)) {
      return triStateSortValue(conv.customerMetadata?.medicationList)
    }
    return -1
  },

  /**
   * Extract assigned staff ID for sorting
   */
  assigned: (conv: AnyConversation): string => {
    if (hasAssignment(conv)) {
      return conv.assignedStaffId ?? ""
    }
    return ""
  },

  /**
   * Extract queue priority/start time for sorting
   */
  queuePriority: (conv: AnyConversation): string => {
    if (hasQueue(conv)) {
      return conv.queuePriority?.toString() || ""
    }
    return ""
  },

  /**
   * Extract created date for sorting
   */
  created: (conv: AnyConversation): string => {
    if (hasTiming(conv)) {
      return conv.created || ""
    }
    return ""
  },

  /**
   * Extract booking start time for sorting
   */
  bookingStart: (conv: AnyConversation): string => {
    if (hasBooking(conv)) {
      return conv.bookingStart || ""
    }
    return ""
  },

  /**
   * Extract paused/snoozed status for sorting
   */
  paused: (conv: AnyConversation): number => {
    if (hasTiming(conv)) {
      return !!conv.snoozedUntil ? 1 : 0
    }
    return 0
  },

  /**
   * Extract category name for sorting (requires context)
   */
  category: (conv: AnyConversation, context: SortUtilsContext): string => {
    if (!context.categories || !hasCategory(conv) || !conv.categoryId) {
      return ""
    }
    return context.categories[conv.categoryId]?.name?.toLowerCase?.() || ""
  },

  /**
   * Extract booking type for sorting (requires context)
   */
  bookingType: (conv: AnyConversation, context: SortUtilsContext): string => {
    if (!context.bookingTypes || !context.appLanguage || !hasBooking(conv) || !conv.bookingFilter) {
      return ""
    }

    try {
      const { kind_id: kindId } = JSON.parse(conv.bookingFilter)
      const bookingType = context.bookingTypes.find(k => k.id === kindId)
      return bookingType?.localisedNames[context.appLanguage]?.toLowerCase?.() || ""
    } catch {
      return ""
    }
  },

  /**
   * Extract booking type for assigned conversations (requires context and bookings)
   */
  assignedBookingType: (conv: AnyConversation, context: SortUtilsContext & { bookings?: Booking[] }): string => {
    if (!context.bookingTypes || !context.appLanguage || !context.bookings || !hasBooking(conv)) {
      return ""
    }

    const booking = context.bookings.find(b => b.id === conv.metadata?.bookingId)
    if (!booking) {
      return ""
    }

    const bookingType = context.bookingTypes.find(k => k.id === booking.kindId)
    return bookingType?.localisedNames[context.appLanguage]?.toLowerCase?.() || ""
  }
}

/**
 * Helper function to create sortValues object using the predefined functions
 * @param conv - The conversation object
 * @param fields - Array of field names to include in sortValues
 * @param context - Additional context needed for some fields (categories, booking types, etc.)
 * @returns sortValues object ready to use in ConversationListItem
 */
export const createSortValues = (
  conv: AnyConversation,
  fields: (keyof typeof sortValueFunctions)[],
  context: SortUtilsContext = {}
): Record<string, string | number> => {
  const sortValues: Record<string, string | number> = {}

  for (const field of fields) {
    const sortFunction = sortValueFunctions[field]
    if (sortFunction) {
      // Handle functions that require context
      if (field === "category" || field === "bookingType" || field === "assignedBookingType") {
        sortValues[field] = sortFunction(conv, context)
      } else {
        sortValues[field] = sortFunction(conv, context)
      }
    }
  }
  return sortValues
}

/**
 * Convenience function for the most common sortValues pattern
 */
export const createCommonSortValues = (
  conv: AnyConversation,
  context: SortUtilsContext = {}
): Record<string, string | number> => {
  return createSortValues(conv, ["pnr", "name", "listed", "recordConsent", "medicationConsent", "assigned"], context)
}

export const triStateSortValue = <T>(value: T | undefined, toBoolean?: (v: T) => boolean): number => {
  if (value === undefined) {
    return -1
  }
  const booleanValue = toBoolean ? toBoolean(value as T) : Boolean(value)
  return booleanValue ? 1 : 0
}
